import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listGiftCards } from "../api/giftCards";
import GiftCardDetailsModal from "../components/modals/GiftCardDetailsModal";
import ModalCloseX from "../components/common/ModalCloseX";
import { formatShortDate } from "../utils/formatShortDate";
import { getSalonTimezone } from "../utils/salonTimezone";

const PER_PAGE_OPTIONS = [10, 25, 50, 100, 200];
const modalText = "text-base md:text-lg";
const labelText = "text-sm md:text-base font-semibold tracking-wide text-slate-500";
const fieldClass =
  "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";
const btnPill =
  "h-10 md:h-11 px-5 rounded-full text-sm md:text-base font-semibold transition";
const btnPrimary =
  `${btnPill} bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:hover:bg-slate-900`;
const btnOutline =
  `${btnPill} border border-slate-300 text-slate-700 hover:bg-slate-50`;

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

function formatDateISO(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateFromParts(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function addDaysToIsoDate(dateStr, deltaDays) {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateFromParts(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate()
  );
}

function validateDateRange(startValue, endValue) {
  if (!startValue || !endValue) return "";
  if (endValue < startValue) {
    return "End date cannot be before start date.";
  }
  return "";
}

function buildPageRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const output = [];
  let last = 0;
  for (const page of sorted) {
    if (page - last > 1) {
      output.push("ellipsis");
    }
    output.push(page);
    last = page;
  }
  return output;
}

function paginateRows(rows, page, perPage) {
  const total = rows.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, total);
  return {
    rows: rows.slice(startIndex, endIndex),
    total,
    startIndex: total === 0 ? 0 : startIndex + 1,
    endIndex,
  };
}

export default function ManageGiftCards() {
  const navigate = useNavigate();
  const { user, currentSalon } = useAuth();
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [activePage, setActivePage] = useState(1);
  const [expiredPage, setExpiredPage] = useState(1);
  const [showExpired, setShowExpired] = useState(false);
  const [sortBy, setSortBy] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedCode, setSelectedCode] = useState(null);
  const [searchCode, setSearchCode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    code: "",
    startDate: "",
    endDate: "",
  });

  const salonId =
    user?.salon_id ||
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = useMemo(
    () => formatDateISO(new Date(), salonTimezone),
    [salonTimezone]
  );
  const dateRangeError = useMemo(
    () => validateDateRange(startDate, endDate),
    [startDate, endDate]
  );

  useEffect(() => {
    setActivePage(1);
    setExpiredPage(1);
  }, [perPage, showExpired, sortBy, sortDir]);

  const applyFilters = (nextCode, nextStart, nextEnd) => {
    const rangeError = validateDateRange(nextStart, nextEnd);
    if (rangeError) return;
    setAppliedFilters({
      code: nextCode || "",
      startDate: nextStart || "",
      endDate: nextEnd || "",
    });
    setActivePage(1);
    setExpiredPage(1);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          include_expired: showExpired,
          per_page: 200,
          sort_by: sortBy,
          sort_dir: sortDir,
        };
        if (appliedFilters.code) {
          params.q = appliedFilters.code;
        }
        if (appliedFilters.startDate) {
          params.start_date = appliedFilters.startDate;
        }
        if (appliedFilters.endDate) {
          params.end_date = appliedFilters.endDate;
        }
        if (salonId) {
          params.salon_id = salonId;
        }
        let page = 1;
        let total = 0;
        const all = [];
        while (true) {
          const data = await listGiftCards({ ...params, page });
          const items = Array.isArray(data.items) ? data.items : [];
          total = Number(data.total || 0);
          all.push(...items);
          if (all.length >= total || items.length === 0) {
            break;
          }
          page += 1;
        }
        if (isMounted) {
          setGiftCards(all);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Unable to load gift cards.");
          setGiftCards([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchAll();
    return () => {
      isMounted = false;
    };
  }, [showExpired, sortBy, sortDir, salonId, appliedFilters]);

  const activeCards = useMemo(() => {
    if (!showExpired) return giftCards;
    return giftCards.filter((card) => !card.is_expired);
  }, [giftCards, showExpired]);

  const expiredCards = useMemo(() => {
    if (!showExpired) return [];
    return giftCards.filter((card) => card.is_expired);
  }, [giftCards, showExpired]);

  const activePageInfo = useMemo(
    () => paginateRows(activeCards, activePage, perPage),
    [activeCards, activePage, perPage]
  );
  const expiredPageInfo = useMemo(
    () => paginateRows(expiredCards, expiredPage, perPage),
    [expiredCards, expiredPage, perPage]
  );

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDir(key === "purchased_at" ? "desc" : "asc");
  };

  const renderSortLabel = (label, key) => {
    const indicator =
      sortBy === key ? (sortDir === "asc" ? " ^" : " v") : "";
    return (
      <button
        type="button"
        onClick={() => handleSort(key)}
        className="text-slate-600 hover:text-slate-900"
      >
        {label}
        {indicator}
      </button>
    );
  };

  const renderPagination = (page, totalRows, setPage) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
    const pages = buildPageRange(page, totalPages);
    return (
      <div className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {"<"}
        </button>
        {pages.map((entry, idx) =>
          entry === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => setPage(entry)}
              className={`h-10 w-10 rounded-full border text-sm md:text-base ${entry === page
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
            >
              {entry}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {">"}
        </button>
      </div>
    );
  };

  const renderTable = (rows, isExpiredSection) => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <table className="min-w-full">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">
              {renderSortLabel(
                isExpiredSection ? "Expired Gift Card" : "Gift Card",
                "code"
              )}
            </th>
            <th className="px-3 py-2">{renderSortLabel("Owner", "owner")}</th>
            <th className="px-3 py-2">
              {renderSortLabel("Total Amount", "total_amount")}
            </th>
            <th className="px-3 py-2">
              {renderSortLabel("Remaining", "remaining")}
            </th>
            <th className="px-3 py-2">
              {renderSortLabel("Purchased on/by", "purchased_at")}
            </th>
          </tr>
        </thead>
        <tbody className="text-sm md:text-base text-slate-800">
          {rows.map((card) => (
            <tr key={card.code} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => setSelectedCode(card.code)}
                  className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-2 hover:text-rose-600"
                >
                  {card.code}
                </button>
              </td>
              <td className="px-3 py-2">{card.owner_name || ""}</td>
              <td className="px-3 py-2">{formatMoney(card.total_amount)}</td>
              <td className="px-3 py-2">{formatMoney(card.remaining)}</td>
              <td className="px-3 py-2">
                {formatShortDate(card.purchased_at)}{" "}
                {card.purchased_by_name ? `by ${card.purchased_by_name}` : ""}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={5}
                className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
              >
                No gift cards found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const handleSearch = () => {
    if (dateRangeError) return;
    applyFilters(searchCode.trim(), startDate, endDate);
  };

  const handleClear = () => {
    setSearchCode("");
    setStartDate("");
    setEndDate("");
    applyFilters("", "", "");
  };

  const handleQuickPick = (range) => {
    if (!today) return;
    if (range === "today") {
      setStartDate(today);
      setEndDate(today);
      applyFilters(searchCode.trim(), today, today);
      return;
    }
    if (range === "last7") {
      const start = addDaysToIsoDate(today, -6);
      setStartDate(start);
      setEndDate(today);
      applyFilters(searchCode.trim(), start, today);
      return;
    }
    if (range === "thisMonth") {
      const parts = today.split("-");
      if (parts.length !== 3) return;
      const start = formatDateFromParts(parts[0], parts[1], 1);
      setStartDate(start);
      setEndDate(today);
      applyFilters(searchCode.trim(), start, today);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
      <div
        className={`relative w-full rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${modalText}`}
      >
        <div className="flex items-center justify-between bg-rose-500 px-6 py-4 pr-20 relative">
          <h1 className="text-xl md:text-2xl font-semibold text-white">
            Gift Cards
          </h1>
          <button
            type="button"
            onClick={() => setShowExpired((prev) => !prev)}
            className={`${btnPill} mr-8 border border-white/30 bg-white/10 text-white hover:bg-white/20`}
          >
            {showExpired ? "Hide expired" : "Show expired"}
          </button>
          <div
            className="absolute right-4 top-3 z-50"
            onClick={handleClose}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <ModalCloseX onClick={handleClose} />
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5 space-y-3">
            <div className={`${labelText} uppercase`}>Filters</div>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="flex flex-col gap-1">
                <label className={labelText}>Gift Card Code</label>
                <input
                  type="text"
                  value={searchCode}
                  onChange={(event) => setSearchCode(event.target.value)}
                  className={fieldClass}
                  placeholder="Search code e.g. 00014"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className={labelText}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className={fieldClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className={labelText}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className={fieldClass}
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={Boolean(dateRangeError)}
                  className={btnPrimary}
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className={btnOutline}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <span className="text-slate-500">Quick pick:</span>
              <button
                type="button"
                onClick={() => handleQuickPick("today")}
                className="text-rose-600 font-semibold hover:text-rose-700"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handleQuickPick("last7")}
                className="text-rose-600 font-semibold hover:text-rose-700"
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={() => handleQuickPick("thisMonth")}
                className="text-rose-600 font-semibold hover:text-rose-700"
              >
                This month
              </button>
            </div>
            {dateRangeError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {dateRangeError}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-700">
              <span className={`${labelText} uppercase`}>Show</span>
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className={fieldClass}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className={`${labelText} uppercase`}>entries</span>
            </div>
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
              {error}
            </div>
          )}

          {loading ? null : (
            <div className="space-y-6">
              {showExpired && (
                <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
                  <div className={`${labelText} uppercase`}>
                    Expired Gift Cards
                  </div>
                  {renderPagination(
                    expiredPage,
                    expiredPageInfo.total,
                    setExpiredPage
                  )}
                  {renderTable(expiredPageInfo.rows, true)}
                  <div className="text-xs md:text-sm text-slate-500">
                    Showing {expiredPageInfo.startIndex} to{" "}
                    {expiredPageInfo.endIndex} of {expiredPageInfo.total} entries
                  </div>
                </div>
              )}

              <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
                <div className={`${labelText} uppercase`}>Gift Cards</div>
                {renderPagination(activePage, activePageInfo.total, setActivePage)}
                {renderTable(activePageInfo.rows, false)}
                <div className="text-xs md:text-sm text-slate-500">
                  Showing {activePageInfo.startIndex} to{" "}
                  {activePageInfo.endIndex} of {activePageInfo.total} entries
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <GiftCardDetailsModal
        isOpen={Boolean(selectedCode)}
        code={selectedCode}
        onClose={() => setSelectedCode(null)}
      />
      </div>
    </div>
  );
}
